import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { EventsContext } from "../services/EventsContext";
import { Helmet } from "react-helmet-async";

import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import SVGSeparator from "../services/SVGSeparator";
import background from "../assets/img/beaufond.jpg";

const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
};

const defaultCenter = { lat: 46.603354, lng: 1.888334 };

// Icônes par catégorie
const categoryIcons = {
  supermarche: "https://cdn-icons-png.flaticon.com/128/2331/2331970.png",
  restaurant: "https://cdn-icons-png.flaticon.com/128/1046/1046784.png",
  cave: "https://cdn-icons-png.flaticon.com/128/2969/2969202.png",
  default: "https://cdn-icons-png.flaticon.com/128/684/684908.png",
};

// ——— Helpers ———
const n = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();



const buildFullAddress = (p) => {
  // Nouveau schéma
  if (p.address1 || p.postalCode || p.country) {
    return [
      p.address1,
      p.address2,
      [p.postalCode, p.city].filter(Boolean).join(" "),
      p.country || "France",
    ]
      .filter(Boolean)
      .join(", ");
  }
  // Ancien schéma
  return [p.address, p.city, "France"].filter(Boolean).join(", ");
};

const pickBestFR = (results, postalCode, city) => {
  if (!results?.length) return null;
  const get = (r, type) =>
    r.address_components.find((c) => c.types.includes(type))?.long_name || "";
  // 1. match CP + ville
  const both = results.find(
    (r) =>
      get(r, "postal_code") === postalCode &&
      n(get(r, "locality") || get(r, "postal_town")) === n(city)
  );
  if (both) return both;
  // 2. match CP
  const byCP = results.find((r) => get(r, "postal_code") === postalCode);
  if (byCP) return byCP;
  // 3. match ville
  const byCity = results.find(
    (r) => n(get(r, "locality") || get(r, "postal_town")) === n(city)
  );
  return byCity || results[0];
};

const FR_BOUNDS = { sw: { lat: 41.0, lng: -5.5 }, ne: { lat: 51.5, lng: 9.7 } };
const inFR = (lat, lng) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  lat >= FR_BOUNDS.sw.lat &&
  lat <= FR_BOUNDS.ne.lat &&
  lng >= FR_BOUNDS.sw.lng &&
  lng <= FR_BOUNDS.ne.lng;

const SalesPointsMap = () => {
  const { salesPoints } = useContext(EventsContext);
  const { t } = useTranslation();

  const mapRef = useRef(null);

  const [search, setSearch] = useState("");
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [geocodedPoints, setGeocodedPoints] = useState([]);

  // Charge la carte via Google Maps API
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCZKd9GsscBfVyRmXv1azAVbTlh0qPnfGc",
    language: "fr",
    region: "FR",
  });

  useEffect(() => {
    if (!isLoaded || !salesPoints?.length) return;

    const geocoder = new window.google.maps.Geocoder();

    Promise.all(
      salesPoints.map((point) => {
        // 1) Si lat/lng valides → on les conserve
        if (inFR(point.lat, point.lng)) {
          return Promise.resolve({
            ...point,
            category: point.category || "default",
          });
        }

        // 2) Sinon, on géocode en France
        const address = buildFullAddress(point);

        return new Promise((resolve) => {
          geocoder.geocode(
            { address, componentRestrictions: { country: "FR" } },
            (results, status) => {
              if (status === "OK" && results?.length) {
                const best = pickBestFR(results, point.postalCode, point.city);
                const loc = (best?.geometry || results[0].geometry).location;
                resolve({
                  ...point,
                  lat: loc.lat(),
                  lng: loc.lng(),
                  category: point.category || "default",
                });
              } else {
                console.warn(
                  `Geocode KO (${status}) pour:`,
                  address,
                  "→ point ignoré pour la carte"
                );
                resolve({ ...point, category: point.category || "default" });
              }
            }
          );
        });
      })
    ).then((points) => {
      setGeocodedPoints(points);

      // Fit bounds sur les points valides
      const usable = points.filter((p) => inFR(p.lat, p.lng));
      if (usable.length && mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        usable.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        mapRef.current.fitBounds(bounds);
      }
    });
  }, [isLoaded, salesPoints]);


  useEffect(() => {
    const q = fold(search);
    if (!q) {
      setFiltered(geocodedPoints);
      return;
    }
    const tokens = q.split(/\s+/).filter(Boolean); // ex. "guimety 84340"
    const out = (geocodedPoints || []).filter((p) => {
      const h = hay(p);
      return tokens.every((t) => h.includes(t));
    });
    setFiltered(out);
  }, [search, geocodedPoints]);

// helper robuste (accents → sans accents, lowercase)
const fold = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

    

// calcule la pile de texte searchable d’un point
const hay = (p) =>
  fold(
    [
      p.name,
      p.address1,
      p.address2,
      p.address,            // compat ancien schéma
      p.formattedAddress,
      p.postalCode,
      p.city,
      p.country,
    ]
      .filter(Boolean)
      .join(" ")
  );

// ⬇️ AJOUTE cet état à côté de tes autres useState
const [filtered, setFiltered] = useState([]);

// Filtrage accent-insensible, multi-mots, sans useMemo
const tokens = fold(search).split(/\s+/).filter(Boolean);

const filteredPoints =
  tokens.length === 0
    ? geocodedPoints
    : geocodedPoints.filter((p) => {
        const haystack = fold(
          [
            p.name,
            p.address1,
            p.address2,
            p.address,            // ancien schéma
            p.formattedAddress,
            p.postalCode,
            p.city,
            p.country,
          ]
            .filter(Boolean)
            .join(" ")
        );
        return tokens.every((tok) => haystack.includes(tok));
      });


// ensuite SEULEMENT :
if (!isLoaded) return <div>Chargement...</div>;

  return (
    <>
      <Helmet>
        <title>Nos Points de Vente | Au Coing du Ventoux</title>
        <meta
          name="description"
          content="Trouvez un point de vente près de chez vous pour acheter notre alcool de coing artisanal."
        />
        <meta
          property="og:title"
          content="Nos Points de Vente | Au Coing du Ventoux"
        />
        <meta property="og:url" content="https://aucoingduventoux.com/sales" />
        <link rel="canonical" href="https://aucoingduventoux.com/sales" />
      </Helmet>

      {/* Section Héro */}
      <div
        className="relative bg-cover bg-center h-screen"
        style={{ backgroundImage: `url(${background})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            {t("sales.bigtitle")}
          </h1>
          <SVGSeparator width={120} height={50} className="" />
          <p className="mt-4 text-lg md:text-xl max-w-3xl">
            {t("sales.bigdescription")}
          </p>
        </div>
      </div>

      <h2 className="text-center  px-6 text-3xl md:text-4xl font-bold text-gray-800 mt-10">
        {t("sales.title")}
      </h2>
      <p className="text-center px-6 py-5"> {t("sales.description")}</p>
      <SVGSeparator width={120} height={50} className="pb-2" />

      <div className="flex flex-col md:flex-row h-screen">
        {/* Liste et recherche */}
        <div className="w-full md:w-1/3 p-8 max-h-[50vh] md:max-h-full mb-10 md:mb-10">
          <input
            type="text"
            placeholder="Rechercher un point de vente..."
            className="border p-2 w-full mb-4"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="space-y-4 max-h-[350px] md:max-h-[400px] overflow-auto border rounded p-4">
            {filtered.map((point) => (
              <li
                key={point.id}
                className="p-4 border rounded shadow cursor-pointer hover:bg-gray-100"
                onClick={() => setSelectedPoint(point)}
              >
                <h3 className="font-bold">{point.name}</h3>
                <p>
                  {point.address ||
                    [point.address1, point.address2]
                      .filter(Boolean)
                      .join(", ")}
                </p>
                <p>
                  {point.postalCode
                    ? `${point.postalCode} ${point.city || ""}`
                    : point.city}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Carte interactive */}
        <div className="w-full md:w-2/3 flex-grow h-[50vh] md:h-full p-8">
          <GoogleMap
            onLoad={(map) => (mapRef.current = map)}
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={defaultCenter}
            zoom={6}
            options={mapOptions}
          >
            {filtered
              .filter((p) => inFR(p.lat, p.lng))
              .map((point) => (
                <Marker
                  key={point.id}
                  position={{ lat: point.lat, lng: point.lng }}
                  onClick={() => setSelectedPoint(point)}
                  icon={{
                    url:
                      (point.category && categoryIcons[point.category]) ||
                      categoryIcons.default,
                    scaledSize: new window.google.maps.Size(40, 40),
                  }}
                  animation={window.google.maps.Animation.DROP}
                />
              ))}

            {selectedPoint && inFR(selectedPoint.lat, selectedPoint.lng) && (
              <InfoWindow
                position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
                onCloseClick={() => setSelectedPoint(null)}
              >
                <div>
                  <h3 className="font-bold">{selectedPoint.name}</h3>
                  <p>
                    {selectedPoint.formattedAddress ||
                      selectedPoint.address ||
                      [selectedPoint.address1, selectedPoint.address2]
                        .filter(Boolean)
                        .join(", ")}
                  </p>
                  <p>
                    {selectedPoint.postalCode
                      ? `${selectedPoint.postalCode} ${selectedPoint.city || ""}`
                      : selectedPoint.city}
                  </p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </div>
      
    </>
    
  );
  
};


export default SalesPointsMap;

